import React, { useEffect, useRef, useState } from 'react';
import { loadGoogleMaps } from '../utils/loadGoogleMaps';
import { Alert, Input } from './ui';

const DEFAULT_CENTER = { lat: -27.4698, lng: 153.0251 };

const isUsableCenter = (value) =>
  value &&
  Number.isFinite(value.lat) &&
  Number.isFinite(value.lng) &&
  value.lat >= -90 &&
  value.lat <= 90 &&
  value.lng >= -180 &&
  value.lng <= 180 &&
  !(value.lat === 0 && value.lng === 0);

const ServiceAreaMap = ({ apiKey, center, radiusKm, initialAddress, onPlaceSelected }) => {
  const mapEl = useRef(null);
  const inputEl = useRef(null);
  const map = useRef(null);
  const marker = useRef(null);
  const circle = useRef(null);
  const autocomplete = useRef(null);
  const repairedInitialAddress = useRef(false);
  const [err, setErr] = useState('');

  const showPoint = (position) => {
    if (!map.current || !marker.current || !circle.current || !isUsableCenter(position)) return;
    map.current.panTo(position);
    map.current.setZoom(11);
    marker.current.setPosition(position);
    marker.current.setMap(map.current);
    circle.current.setCenter(position);
    circle.current.setMap(map.current);
  };

  useEffect(() => {
    let cancelled = false;
    if (!apiKey) {
      setErr('Google Maps API key is missing. Add REACT_APP_GOOGLE_MAPS_API_KEY to your .env.');
      return undefined;
    }

    loadGoogleMaps(apiKey)
      .then((maps) => {
        if (cancelled || !mapEl.current) return;
        const hasCenter = isUsableCenter(center);
        const start = hasCenter ? center : DEFAULT_CENTER;

        map.current = new maps.Map(mapEl.current, {
          center: start,
          zoom: hasCenter ? 11 : 9,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });
        marker.current = new maps.Marker({ map: hasCenter ? map.current : null, position: start });
        circle.current = new maps.Circle({
          map: hasCenter ? map.current : null,
          center: start,
          radius: (radiusKm || 0) * 1000,
          strokeColor: '#209668',
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: '#209668',
          fillOpacity: 0.12,
        });

        if (inputEl.current) {
          autocomplete.current = new maps.places.Autocomplete(inputEl.current, {
            fields: ['formatted_address', 'geometry'],
            componentRestrictions: { country: 'au' },
          });
          autocomplete.current.addListener('place_changed', () => {
            const place = autocomplete.current.getPlace();
            if (!place.geometry?.location) return;
            const next = {
              address: place.formatted_address || inputEl.current.value,
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
            };
            showPoint({ lat: next.lat, lng: next.lng });
            setErr('');
            onPlaceSelected?.(next);
          });
        }

        // Legacy accounts may contain [0,0] or no GeoJSON point even though an
        // address was saved. Resolve that existing address once so the provider
        // sees the real service-area circle and saving the form repairs the data.
        if (!hasCenter && initialAddress && !repairedInitialAddress.current) {
          repairedInitialAddress.current = true;
          const geocoder = new maps.Geocoder();
          geocoder.geocode({ address: initialAddress, componentRestrictions: { country: 'AU' } }, (results, status) => {
            if (cancelled || status !== 'OK' || !results?.[0]?.geometry?.location) return;
            const result = results[0];
            const next = {
              address: result.formatted_address || initialAddress,
              lat: result.geometry.location.lat(),
              lng: result.geometry.location.lng(),
            };
            if (inputEl.current) inputEl.current.value = next.address;
            showPoint({ lat: next.lat, lng: next.lng });
            onPlaceSelected?.(next);
          });
        }
      })
      .catch(() => {
        if (!cancelled) setErr('Could not load Google Maps. Check the key and that Maps JavaScript API + Places API are enabled.');
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey]);

  useEffect(() => {
    if (inputEl.current && initialAddress && !inputEl.current.value) inputEl.current.value = initialAddress;
  }, [initialAddress]);

  useEffect(() => {
    if (!isUsableCenter(center)) return;
    showPoint(center);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center?.lat, center?.lng]);

  useEffect(() => {
    if (circle.current) circle.current.setRadius((radiusKm || 0) * 1000);
  }, [radiusKm]);

  return (
    <div className="flex flex-col gap-3">
      <Input ref={inputEl} placeholder="Search your address…" />
      {err && <Alert variant="danger">{err}</Alert>}
      <div ref={mapEl} className="h-80 w-full overflow-hidden rounded-xl border border-slate-200 bg-slate-50" />
    </div>
  );
};

export default ServiceAreaMap;
